/**
 * AdminLogoUploadButton — inline "upload a logo" widget for admins.
 *
 * Two-step flow behind a single click:
 *   1. POST /api/upload/image?image_type=business       (multipart file)
 *   2. PATCH /api/admin/businesses/{business_id}         (write logo_url)
 *
 * Rendered as a compact icon+label button so it fits next to the Edit
 * button on each row of the Admin Businesses tab, the Verifications tab,
 * and the Image Health "still missing" panel.
 *
 * Props:
 *   - businessId  (required)
 *   - businessName — used in the success toast
 *   - size        — "sm" (default) | "xs"
 *   - variant     — "outline" (default) | "ghost"
 *   - onUploaded  — callback(newLogoUrl) so the parent can refresh its row
 */
import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";

export default function AdminLogoUploadButton({
  businessId,
  businessName = "business",
  size = "sm",
  variant = "outline",
  onUploaded,
  label = "Upload logo",
  className = "",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => {
    if (busy) return;
    inputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // reset so the same file can be re-picked
    if (!file) return;

    // Client-side sanity checks — backend re-validates too
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file (PNG, JPG, WEBP, SVG).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo must be under 5 MB.");
      return;
    }

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);

      const uploadRes = await axios.post(
        `${API}/upload/image?image_type=business`,
        fd,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      const newUrl = uploadRes.data?.url;
      if (!newUrl) throw new Error("No URL returned from upload");

      await axios.patch(
        `${API}/admin/businesses/${businessId}`,
        { logo_url: newUrl },
        { withCredentials: true }
      );

      toast.success(`Logo uploaded for ${businessName}`);
      onUploaded?.(newUrl);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={pick}
        disabled={busy}
        className={className}
        data-testid={`upload-logo-btn-${businessId}`}
        title={label}
      >
        {busy ? (
          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
        ) : (
          <Upload className="w-3.5 h-3.5 mr-1.5" />
        )}
        {label}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
        data-testid={`upload-logo-input-${businessId}`}
      />
    </>
  );
}
